import React, { useMemo } from 'react';
import { Form, Button, Badge, Table } from 'react-bootstrap';

/**
 * BulkEditDaysView - Table-based view for bulk editing workout days
 *
 * Allows users to select multiple workouts and move them to a different day
 * within their same week. Provides filtering by week and day.
 */
const BulkEditDaysView = ({
  workouts,
  selectedWorkoutIds,
  setSelectedWorkoutIds,
  bulkFilterWeek,
  setBulkFilterWeek,
  bulkFilterDay,
  setBulkFilterDay,
  targetDay,
  setTargetDay,
  onBulkMove,
  inlineTemplates,
  isSaving
}) => {
  // Only show workouts that have a week assigned (can be bulk moved)
  const eligibleWorkouts = useMemo(() => {
    return workouts.filter(w => w.week !== null && w.week !== undefined);
  }, [workouts]);

  // Get unique weeks and days for filters
  const { weeks, days } = useMemo(() => {
    const weekSet = new Set();
    const daySet = new Set();

    eligibleWorkouts.forEach(w => {
      if (w.week !== null && w.week !== undefined) weekSet.add(w.week);
      if (w.day !== null && w.day !== undefined) daySet.add(w.day);
    });

    return {
      weeks: Array.from(weekSet).sort((a, b) => a - b),
      days: Array.from(daySet).sort((a, b) => a - b)
    };
  }, [eligibleWorkouts]);

  // Apply filters
  const filteredWorkouts = useMemo(() => {
    return eligibleWorkouts.filter(w => {
      if (bulkFilterWeek !== '' && w.week !== parseInt(bulkFilterWeek)) return false;
      if (bulkFilterDay !== '' && w.day !== parseInt(bulkFilterDay)) return false;
      return true;
    });
  }, [eligibleWorkouts, bulkFilterWeek, bulkFilterDay]);

  // Get template name for a workout
  const getTemplateName = (workout) => {
    if (workout.template_id) {
      const template = inlineTemplates?.find(t => t.id === workout.template_id);
      return template?.name || 'Unknown Template';
    }
    return workout.template_name || '-';
  };

  // Handle select all toggle
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const newSelected = new Set(selectedWorkoutIds);
      filteredWorkouts.forEach(w => newSelected.add(w.id));
      setSelectedWorkoutIds(newSelected);
    } else {
      const newSelected = new Set(selectedWorkoutIds);
      filteredWorkouts.forEach(w => newSelected.delete(w.id));
      setSelectedWorkoutIds(newSelected);
    }
  };

  // Handle individual checkbox toggle
  const handleToggleWorkout = (workoutId) => {
    const newSelected = new Set(selectedWorkoutIds);
    if (newSelected.has(workoutId)) {
      newSelected.delete(workoutId);
    } else {
      newSelected.add(workoutId);
    }
    setSelectedWorkoutIds(newSelected);
  };

  // Check if all filtered workouts are selected
  const allFilteredSelected = filteredWorkouts.length > 0 &&
    filteredWorkouts.every(w => selectedWorkoutIds.has(w.id));

  // Check if some (but not all) filtered workouts are selected
  const someFilteredSelected = filteredWorkouts.some(w => selectedWorkoutIds.has(w.id)) &&
    !allFilteredSelected;

  // Get selected workouts info
  const selectedWorkouts = workouts.filter(w => selectedWorkoutIds.has(w.id));
  const selectedWeeks = [...new Set(selectedWorkouts.map(w => w.week))].sort((a, b) => a - b);

  // Clear selection
  const handleClearSelection = () => {
    setSelectedWorkoutIds(new Set());
  };

  // Handle move button click
  const handleMoveClick = () => {
    const targetDayNum = parseInt(targetDay);
    if (isNaN(targetDayNum) || targetDayNum < 1 || targetDayNum > 7) {
      alert('Please enter a valid target day (1-7)');
      return;
    }
    if (selectedWorkoutIds.size === 0) {
      alert('Please select at least one workout to move');
      return;
    }
    onBulkMove(targetDayNum);
  };

  if (eligibleWorkouts.length === 0) {
    return (
      <div className="text-center text-muted py-4">
        <i className="fas fa-calendar-times fa-3x mb-3"></i>
        <p>No workouts with weeks assigned. Bulk edit requires workouts to have a week value.</p>
      </div>
    );
  }

  return (
    <div className="bulk-edit-days-view">
      {/* Filter and Action Bar */}
      <div className="bulk-edit-toolbar mb-3">
        <div className="d-flex flex-wrap align-items-center gap-3">
          {/* Week Filter */}
          <Form.Group className="d-flex align-items-center gap-2">
            <Form.Label className="mb-0 text-nowrap">Week:</Form.Label>
            <Form.Select
              size="sm"
              value={bulkFilterWeek}
              onChange={(e) => setBulkFilterWeek(e.target.value)}
              style={{ width: 'auto', minWidth: '100px' }}
            >
              <option value="">All Weeks</option>
              {weeks.map(w => (
                <option key={w} value={w}>Week {w}</option>
              ))}
            </Form.Select>
          </Form.Group>

          {/* Day Filter */}
          <Form.Group className="d-flex align-items-center gap-2">
            <Form.Label className="mb-0 text-nowrap">Day:</Form.Label>
            <Form.Select
              size="sm"
              value={bulkFilterDay}
              onChange={(e) => setBulkFilterDay(e.target.value)}
              style={{ width: 'auto', minWidth: '100px' }}
            >
              <option value="">All Days</option>
              {days.map(d => (
                <option key={d} value={d}>Day {d}</option>
              ))}
            </Form.Select>
          </Form.Group>

          <div className="vr d-none d-md-block"></div>

          {/* Target Day Input */}
          <Form.Group className="d-flex align-items-center gap-2">
            <Form.Label className="mb-0 text-nowrap">Move to Day:</Form.Label>
            <Form.Control
              type="number"
              size="sm"
              min="1"
              max="7"
              value={targetDay}
              onChange={(e) => setTargetDay(e.target.value)}
              placeholder="1-7"
              style={{ width: '70px' }}
            />
          </Form.Group>

          {/* Move Button */}
          <Button
            variant="primary"
            size="sm"
            onClick={handleMoveClick}
            disabled={isSaving || selectedWorkoutIds.size === 0 || !targetDay}
          >
            <i className="fas fa-arrows-alt me-1"></i>
            Move {selectedWorkoutIds.size} to Day {targetDay || '?'}
          </Button>
        </div>
      </div>

      {/* Selection Info */}
      {selectedWorkoutIds.size > 0 && (
        <div className="bulk-edit-selection-info mb-3 d-flex justify-content-between align-items-center">
          <span>
            <Badge bg="primary" className="me-2">{selectedWorkoutIds.size}</Badge>
            workout{selectedWorkoutIds.size !== 1 ? 's' : ''} selected
            {selectedWeeks.length > 0 && (
              <span className="text-muted ms-2">
                (Week{selectedWeeks.length > 1 ? 's' : ''}: {selectedWeeks.join(', ')})
              </span>
            )}
          </span>
          <Button variant="outline-secondary" size="sm" onClick={handleClearSelection}>
            Clear Selection
          </Button>
        </div>
      )}

      {/* Workouts Table */}
      <div className="bulk-edit-table-container">
        <Table striped bordered hover size="sm" className="bulk-edit-table">
          <thead>
            <tr>
              <th style={{ width: '40px' }}>
                <Form.Check
                  type="checkbox"
                  checked={allFilteredSelected}
                  ref={el => {
                    if (el) el.indeterminate = someFilteredSelected;
                  }}
                  onChange={handleSelectAll}
                  disabled={isSaving}
                />
              </th>
              <th style={{ width: '80px' }}>Week</th>
              <th style={{ width: '70px' }}>Day</th>
              <th>Workout Name</th>
              <th>Template</th>
            </tr>
          </thead>
          <tbody>
            {filteredWorkouts.map(workout => (
              <tr
                key={workout.id}
                className={selectedWorkoutIds.has(workout.id) ? 'table-primary' : ''}
              >
                <td>
                  <Form.Check
                    type="checkbox"
                    checked={selectedWorkoutIds.has(workout.id)}
                    onChange={() => handleToggleWorkout(workout.id)}
                    disabled={isSaving}
                  />
                </td>
                <td>{workout.week}</td>
                <td>{workout.day ?? '-'}</td>
                <td>{workout.name || 'Unnamed Workout'}</td>
                <td>{getTemplateName(workout)}</td>
              </tr>
            ))}
          </tbody>
        </Table>

        {filteredWorkouts.length === 0 && (
          <div className="text-center text-muted py-4">
            <p>No workouts match the current filters.</p>
          </div>
        )}
      </div>

      {/* Help Text */}
      <div className="bulk-edit-help mt-3">
        <small className="text-muted">
          <i className="fas fa-info-circle me-1"></i>
          Select workouts and move them to a different day. Workouts stay in their current week.
        </small>
      </div>
    </div>
  );
};

export default BulkEditDaysView;
